import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, Subject, takeUntil } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { parseJwt } from 'src/app/utilities/fn';

/**
 * Header shell theo mockup; template dùng `translate`, `saveLang`, `emitMenuClick`, `logoutClick`.
 */
@Component({
  selector: 'app-shell-header-mockup',
  templateUrl: './shell-header-mockup.component.html',
  styleUrls: ['./shell-header-mockup.component.scss'],
})
export class ShellHeaderMockupComponent implements OnInit, OnDestroy {
  @Output() menuClick = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  @Input() companyName = 'Công ty CP Công Nghệ SHRM';
  @Input() searchPlaceholder = 'Tìm kiếm nhân viên, hợp đồng... (⌘K)';

  userName = '';
  isModuleDropdownOpen = false;
  isUserMenuOpen = false;
  isLanguageDropdownOpen = false;
  isDarkMode = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    private authService: AuthService,
    private readonly oauthService: OAuthService,
    private apiService: ApiService,
    private languageService: LanguageService,
    private spinner: NgxSpinnerService,
  ) {}

  get userEmail(): string {
    const t = parseJwt(this.oauthService.getAccessToken());
    return (t.email as string) || (t.preferred_username as string) || '';
  }

  get avatarUrl(): string {
    const u = this.authService.getUserImage();
    return u || 'assets/images/walter.png';
  }

  ngOnInit(): void {
    this.userName = this.authService.getUserName();
    const savedTheme = localStorage.getItem('theme');
    this.isDarkMode = savedTheme === 'dark';
    this.applyTheme();

    from(this.languageService.getLanguagePromise()).subscribe(() => {
      const language = this.languageService.getLanguage();
      this.translate.addLangs(['vi', 'en', 'cn', 'jn']);
      if (language) {
        this.translate.use(language);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
    this.applyTheme();
  }

  applyTheme(): void {
    const el = document.documentElement;
    if (this.isDarkMode) {
      el.classList.add('dark');
    } else {
      el.classList.remove('dark');
    }
  }

  saveLang(lang: 'vi' | 'en' | 'cn' | 'jn'): void {
    this.isLanguageDropdownOpen = false;
    this.apiService
      .setUserLanguage({ acceptLanguage: lang })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results: any) => {
          if (results.status === 'success') {
            this.languageService.setLanguage(lang);
            this.translate.use(lang);
            localStorage.setItem('currentLang', lang);
            localStorage.setItem('language', lang);
            window.location.reload();
          } else {
            this.spinner.hide();
          }
        },
        () => {
          this.spinner.hide();
        },
      );
  }

  emitMenuClick(): void {
    this.menuClick.emit();
  }

  toggleLanguageDropdown(): void {
    this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
  }

  toggleModuleDropdown(): void {
    this.isModuleDropdownOpen = !this.isModuleDropdownOpen;
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  closeLanguageBackdrop(): void {
    this.isLanguageDropdownOpen = false;
  }

  closeModuleBackdrop(): void {
    this.isModuleDropdownOpen = false;
  }

  closeUserBackdrop(): void {
    this.isUserMenuOpen = false;
  }

  logoutClick(): void {
    this.isUserMenuOpen = false;
    this.logout.emit();
    this.oauthService.revokeTokenAndLogout(
      {
        client_id: this.oauthService.clientId,
        returnTo: this.oauthService.redirectUri,
      },
      true,
    );
  }
}
